import { UploadBox } from '@/components/UI/Box';
import { StyledDialog } from '@/components/UI/Dialog';
import { TabPanel } from '@mui/lab';
import {
  Autocomplete,
  MobileStepper,
  Paper,
  styled,
  Box,
  CardMedia,
  IconButton,
  Table,
  TableCell,
  TableCellProps,
  TableRow,
  TextField,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
  Tab,
  Chip,
  alpha,
} from '@mui/material';

export const StyledTable = styled(Table)(() => ({
  borderCollapse: 'collapse',
}));

export const RowCaption = styled(TableRow)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.dark,
  border: `1px solid ${theme.palette.primary.dark}`,
}));

export const CellCaption = styled(TableCell)(() => ({
  paddingLeft: '40px',
  fontSize: '21px',
  fontWeight: '600',
  color: '#ECF0F6',
  textAlign: 'center',
}));

export const BoldCell = styled(TableCell)(({ theme }) => ({
  border: `1px solid ${theme.palette.primary.dark}`,
  padding: '8px',
  fontSize: '16px',
  fontWeight: '600',
  color: theme.palette.primary.dark,
}));

export const RowHeader = styled(TableRow)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.light, 0.1),
}));

interface CellHeaderProps extends TableCellProps {
  minwidth?: string;
}

export const CellHeader = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== 'bleft' && prop !== 'bright',
})<CellHeaderProps>(({ minwidth, theme }) => ({
  border: `1px solid ${theme.palette.primary.dark}`,
  padding: '8px',
  fontSize: '16px',
  fontWeight: '600',
  color: theme.palette.primary.dark,
  minWidth: minwidth,
}));

export const CellContent = styled(TableCell)(({ theme }) => ({
  border: `1px solid ${theme.palette.primary.dark}`,
  padding: '8px',
  fontSize: '16px',
  color: theme.palette.primary.dark,
}));

export const TableTextField = {
  width: '100%',
  '& .MuiInputBase-root': {
    borderRadius: '8px',
    fontSize: '16px',
  },
  '& .MuiInputBase-input': {
    padding: '8px',
  },
};

export const HighlightTableTextField = {
  width: '100%',
  '& .MuiInputBase-root': {
    borderRadius: '8px',
    fontSize: '16px',
    backgroundColor: '#FFF9C4',
  },
  '& .MuiInputBase-input': {
    padding: '8px',
  },
};

export const Upload = styled(UploadBox)(() => ({
  width: '400px',
  height: '400px',
  gap: '8px',
  margin: '20px auto',
}));

export const InsideBox = styled(Box)(() => ({
  width: '390px',
  height: '390px',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
}));

export const UploadIcon = styled(Typography)(({ theme }) => ({
  '& span': { fontSize: '72px', color: theme.palette.primary.main },
}));

export const UploadTitle = styled(Typography)(() => ({
  fontWeight: '600',
  fontSize: '14px',
}));

export const UploadSubtitle = styled(Typography)(() => ({
  fontSize: '14px',
}));

export const GraphTitle = styled(Typography)(({ theme }) => ({
  width: '100%',
  display: 'block',
  textAlign: 'center',
  color: theme.palette.primary.dark,
  fontWeight: '600',
  fontSize: '16px',
  marginBottom: '4px',
}));

export const Dialog = styled(StyledDialog)(({ theme }) => ({
  '& .MuiPaper-root': {
    minHeight: '600px',
    padding: '40px 60px 0px',
    [theme.breakpoints.down('xl')]: {
      maxWidth: '1000px',
      minWidth: '900px',
    },
    [theme.breakpoints.up('xl')]: {
      maxWidth: '1100px',
      minWidth: '1000px',
    },
  },
}));

export const CropperContainer = styled(Box)(() => ({
  position: 'relative',
  width: '95%',
  minHeight: '400px',
  maxHeight: '420px',
  margin: '0 auto 10px',
}));

export const StyledCardMedia = styled(CardMedia)(() => ({
  backgroundSize: 'contain',
  height: '400px',
  width: '400px',
  margin: '0 auto',
}));

export const EditCircleButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  margin: '-21px -14px 0',
  backgroundColor: theme.palette.common.grey,
  padding: '6px',
  color: theme.palette.common.white,
  '&:hover': {
    padding: '7px',
    backgroundColor: theme.palette.primary.main,
  },
  zIndex: 10,
}));

export const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    fontSize: 12,
    marginTop: '-24px !important',
    marginBottom: '24px !important',
    marginLeft: '-24px !important',
  },
}));

export const VariableType = styled(Typography)(() => ({
  fontSize: '14px',
  color: '#898484',
  fontStyle: 'italic',
  margin: '15px 8px 0',
}));

export const VariableItem = styled(Typography)(() => ({
  fontSize: '14px',
}));

export const VariableBox = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  borderBottom: ' 1px solid #D0D5DD',
  width: '100%',
  padding: '15px 8px',
}));

export const VariableTextField = styled(TextField)(() => ({
  width: '250px',
  height: '48px',
  '& .MuiInputBase-root': {
    borderRadius: '16px',
  },
  '& .MuiInputBase-input': {
    padding: '13.5px',
  },
}));

export const VariableIconButton = styled(IconButton)(({ theme }) => ({
  width: '52px',
  height: '44px',
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  borderRadius: '10px',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
  '&:disabled': {
    backgroundColor: theme.palette.primary.main,
    opacity: 0.5,
    color: 'white',
  },
}));

export const headerBorder = {
  borderRight: '1px solid #fff',
  fontSize: '19px',
};

export const itemColumnWidth = '120px';

export const columnWidth = '160px';

export const StyledMobileStepper = styled(MobileStepper)(({ theme }) => ({
  width: '100%',
  background: 'transparent',
  padding: 0,
  '& .MuiMobileStepper-progress': {
    width: '100%',
    height: 14,
    borderRadius: '60px',
    backgroundColor: '#FAFCFF',
    border: '1px solid',
    borderColor: theme.palette.primary.main,
    '& .MuiLinearProgress-bar': {
      backgroundColor: theme.palette.primary.main,
      borderRadius: '60px',
    },
  },
}));

export const StyledTabPanel = styled(TabPanel)(() => ({
  backgroundColor: '#F5F5F4',
  boxShadow: '0px 1px 2px #61335429',
  minHeight: '600px',
  padding: '32px',
}));

export const StyledTab = styled(Tab)(({ theme }) => ({
  padding: '12px 24px',
  borderRadius: '20px 20px 0 0',
  color: theme.palette.primary.dark,
  textTransform: 'none',
  fontSize: '16px',
  fontWeight: '600',
  '&.Mui-selected': {
    backgroundColor: '#F5F5F4',
    boxShadow: '0px 1px 2px #61335429',
  },
}));

export const StyledChip = styled(Chip)(() => ({
  fontWeight: 600,
  marginBottom: '16px',
  fontSize: '16px',
  padding: '16px 20px',
}));

export const TabContent = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
}));

export const BreadcrumbsBase = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.dark,
  fontSize: '24px',
  textDecoration: 'underline',
  textDecorationColor: 'transparent',
}));

export const BreadcrumbsTitle = styled(Box)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '24px',
  color: theme.palette.primary.dark,
}));

export const SectionIndexGroupList = styled(Box)(() => ({
  padding: 0,
}));

export const SectionIndexGroupLabel = styled(Box)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  color: theme.palette.primary.dark,
  lineHeight: '32px',
}));

export const SectionIndexOptionLabel = styled(Box)(() => ({
  fontWeight: 500,
}));

export const SectionIndexOptionSubLabel = styled(Box)(({ theme }) => ({
  marginLeft: theme.spacing(1),
  fontSize: 12,
  color: theme.palette.text.secondary,
}));

export const StyledSectionIndexAutocomplete = styled(Autocomplete)(
  ({ theme }) => ({
    marginBottom: theme.spacing(2),
    width: '100%',
    maxWidth: 400,
    alignSelf: 'flex-end',
    '& .MuiInputBase-root': {
      borderRadius: '30px',
    },
    '& .MuiButtonBase-root': {
      marginLeft: '4px',
    },
    '& .MuiSvgIcon-root': {
      color: theme.palette.primary.main,
      fontSize: '1.5rem',
    },
  })
) as typeof Autocomplete;

export const StyledSectionIndexPaper = styled(Paper)(({ theme }) => ({
  '& .MuiAutocomplete-option': {
    color: theme.palette.common.black,
  },
  '& .MuiAutocomplete-option:hover': {
    backgroundColor: `${alpha(theme.palette.primary.main, 0.1)} !important`,
  },
  '& .MuiAutocomplete-option.Mui-focused': {
    backgroundColor: `${alpha(theme.palette.primary.main, 0.1)} !important`,
  },
  '& .MuiAutocomplete-option[aria-selected="true"]': {
    color: theme.palette.primary.main,
  },
  '& .MuiAutocomplete-groupLabel': {
    color: theme.palette.secondary.main,
    fontWeight: 600,
  },
}));

export const SaveButtonBox = styled(Box)(() => ({
  position: 'sticky',
  bottom: 64,
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: 24,
  zIndex: 10,
  pointerEvents: 'none',
  '& > *': {
    pointerEvents: 'auto',
  },
}));
